import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, 
  ExternalLink, 
  Server,
  Users,
  MessageSquare,
  CheckCircle,
  Info
} from 'lucide-react';
import { channelService, agentService } from '../../services/PlatformService';

// Definição dos tipos de canal com visual aprimorado
const CHANNEL_TYPES = [
  {
    id: 'whatsapp_business',
    name: 'WhatsApp Business',
    description: 'Integração oficial com WhatsApp Business API',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg',
    gradient: 'from-green-400 to-green-600',
    shadowColor: 'shadow-green-500/20',
    available: true,
    requiresInstance: false,
    comingSoon: false
  },
  {
    id: 'whatsapp_qr',
    name: 'WhatsApp QR Code',
    description: 'Conecte rapidamente via QR Code',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg',
    gradient: 'from-green-500 to-emerald-600',
    shadowColor: 'shadow-emerald-500/20',
    available: true,
    requiresInstance: true,
    comingSoon: false
  },
  {
    id: 'instagram_direct',
    name: 'Instagram Direct',
    description: 'Atenda pelo Instagram Direct Messages',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png',
    gradient: 'from-green-500 via-pink-500 to-orange-400',
    shadowColor: 'shadow-pink-500/20',
    available: true,
    comingSoon: false,
    requiresInstance: false
  },
  {
    id: 'messenger',
    name: 'Facebook Messenger',
    description: 'Integração com Messenger do Facebook',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/b/be/Facebook_Messenger_logo_2020.svg',
    gradient: 'from-blue-300 to-blue-500',
    shadowColor: 'shadow-indigo-500/20',
    available: true,
    comingSoon: false,
    requiresInstance: false
  },
  {
    id: 'telegram',
    name: 'Telegram',
    description: 'Converse com clientes pelo Telegram',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg',
    gradient: 'from-blue-400 to-green-600',
    shadowColor: 'shadow-blue-500/20',
    available: false,
    comingSoon: true,
    requiresInstance: false
  }
];

// Componente para o card do agente
const AgentCard = ({ agent, isSelected, onClick }) => {
  const getAgentAvatar = (agent) => {
    if (agent.widget_settings?.logo_url) {
      return agent.widget_settings.logo_url;
    }
    return null;
  };

  return (
    <button
      onClick={onClick}
      className={`
        relative w-full bg-white/80 backdrop-blur-sm rounded-3xl p-6
        transition-all duration-200 transform hover:scale-[1.02]
        ${isSelected 
          ? 'border-2 border-indigo-500 ring-4 ring-indigo-100 shadow-2xl' 
          : 'border-2 border-gray-100 hover:border-gray-200 shadow-xl hover:shadow-2xl'
        }
      `}
    >
      {/* Badge de selecionado */}
      {isSelected && (
        <div className="absolute top-4 right-4">
          <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
        </div>
      )}

      {/* Avatar */}
      <div className="flex flex-col items-center">
        <div className="relative">
          <div className={`
            w-32 h-32 rounded-full bg-gradient-to-br from-green-100 to-green-100 p-1
            ${isSelected ? 'ring-4 ring-indigo-200' : ''}
          `}>
            <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center">
              {getAgentAvatar(agent) ? (
                <img 
                  src={getAgentAvatar(agent)} 
                  alt={agent.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-4xl font-bold bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent">
                  {agent.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>
          {/* Status badge */}
          <div className="absolute bottom-0 right-0">
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              Ativo
            </span>
          </div>
        </div>

        {/* Info */}
        <h3 className="mt-4 text-xl font-bold text-gray-900">{agent.name}</h3>
        <p className="mt-1 text-sm text-gray-600 text-center line-clamp-2">
          {agent.description || 'Funcionário digital pronto para atender'}
        </p>
      </div>
    </button>
  );
};

// Componente para o card do canal
const ChannelCard = ({ channel, isSelected, onClick, disabled }) => {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`
        relative w-full bg-white rounded-3xl p-8
        transition-all duration-200 transform
        ${disabled 
          ? 'opacity-60 cursor-not-allowed grayscale' 
          : 'hover:scale-[1.03] cursor-pointer'
        }
        ${isSelected && !disabled
          ? 'border-2 border-indigo-500 ring-4 ring-indigo-100 shadow-2xl' 
          : `border-2 border-gray-100 shadow-xl ${!disabled && 'hover:shadow-2xl hover:border-gray-200'}`
        }
      `}
    >
      {/* Badge de selecionado */}
      {isSelected && !disabled && (
        <div className="absolute top-4 right-4">
          <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
        </div>
      )}

      {/* Coming soon badge */}
      {channel.comingSoon && (
        <div className="absolute top-4 right-4">
          <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
            Em breve
          </span>
        </div>
      )}

      {/* Logo container */}
      <div className={`
        w-24 h-24 mx-auto mb-4 rounded-2xl p-4
        bg-gradient-to-br ${channel.gradient}
        flex items-center justify-center
        ${!disabled && `${channel.shadowColor} shadow-lg`}
      `}>
        <img 
          src={channel.logo} 
          alt={channel.name}
          className="w-16 h-16 object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <MessageSquare className="w-16 h-16 text-white hidden" />
      </div>

      {/* Info */}
      <h3 className="text-xl font-bold text-gray-900 mb-2">{channel.name}</h3>
      <p className="text-sm text-gray-600">{channel.description}</p>

      {/* Requires instance badge */}
      {channel.requiresInstance && !channel.comingSoon && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Server className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-medium text-blue-600">
            Requer Instância WhatsApp
          </span>
        </div>
      )}
    </button>
  );
};

const ChannelNew = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedInstance, setSelectedInstance] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await agentService.getUserAgents();
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error loading agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleSelectAgent = (agentId) => {
    setSelectedAgent(agentId);
    setCurrentStep(2);
  };

  const handleSelectChannel = async (channelType) => {
    setSelectedType(channelType.id);
    
    handleCreateChannel(channelType.id);
  };

  const handleCreateChannel = async (typeId = selectedType, instanceId = selectedInstance) => {
    if (!selectedAgent || !typeId) {
      return;
    }

    setLoading(true);
    try {
      const selectedChannel = CHANNEL_TYPES.find(t => t.id === typeId);
      const selectedAgentData = agents.find(a => a.id === selectedAgent);
      
      const channelData = {
        agent_id: selectedAgent,
        type: typeId,
        name: `${selectedChannel?.name} - ${selectedAgentData?.name}`
      };

      if (typeId === 'whatsapp_qr' && instanceId) {
        channelData.instance_id = instanceId;
      }

      const response = await channelService.createChannel(channelData);
      navigate(`/channels/${response.data.id}/configure`);
    } catch (error) {
      console.error('Error creating channel:', error);
      
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already in use')) {
        alert('Esta instância já está sendo usada por outro canal. Selecione uma instância diferente.');
      } else {
        alert('Erro ao criar canal. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedAgentData = agents.find(a => a.id === selectedAgent);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-800 via-green-600 to-green-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-4xl font-bold">Novo Canal de Atendimento</h1>
          <p className="text-xl text-white/90 mt-2">
            Configure onde seu funcionário digital vai atender os clientes
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 pb-12 pt-8">
        {/* Step 1: Select Agent */}
        {currentStep === 1 && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Escolha o funcionário responsável
              </h2>
              <p className="text-gray-600 flex items-center justify-center gap-2">
                <Info className="w-4 h-4" />
                Não se preocupe, você pode alterar isso depois nas configurações
              </p>
            </div>

            {loadingAgents ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white/80 rounded-3xl p-6 animate-pulse">
                    <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                  </div>
                ))}
              </div>
            ) : agents.length === 0 ? (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-12 text-center">
                <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Comece criando um funcionário digital
                </h3>
                <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                  Você precisa ter pelo menos um funcionário para criar um canal de atendimento
                </p>
                <button
                  onClick={() => navigate('/agent-manager')}
                  className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-600 text-white rounded-2xl font-semibold hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] text-lg"
                >
                  Criar Primeiro Funcionário
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {agents.map(agent => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    isSelected={selectedAgent === agent.id}
                    onClick={() => handleSelectAgent(agent.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Channel */}
        {currentStep === 2 && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Onde {selectedAgentData?.name} vai atender?
              </h2>
              <p className="text-gray-600">
                Escolha a plataforma de mensagens para conectar
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {CHANNEL_TYPES.map(channel => (
                <ChannelCard
                  key={channel.id}
                  channel={channel}
                  isSelected={selectedType === channel.id}
                  onClick={() => handleSelectChannel(channel)}
                  disabled={!channel.available}
                />
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 text-gray-700 font-semibold hover:bg-gray-100 rounded-2xl transition-all"
              >
                ← Voltar para funcionários
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelNew;